import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { setSessionClearHandler } from '../sessionSync';
import type { AuthTokens, AuthUser } from '../interfaces';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (params: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    companyName: string;
    websiteUrl?: string;
  }) => Promise<{ userId: number }>;
  logout: () => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const saveTokens = (tokens: AuthTokens) => {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    setUser(tokens.user);
  };

  const clearTokens = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  const login = async (email: string, password: string) => {
    const tokens = await authService.login(email, password);
    saveTokens(tokens);
  };

  const signup = async (params: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    companyName: string;
    websiteUrl?: string;
  }) => {
    return await authService.signup(params);
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        await authService.logout(refreshToken);
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    clearTokens();
  };

  const verifyEmail = async (token: string) => {
    const tokens = await authService.verifyEmail(token);
    saveTokens(tokens);
  };

  const refreshAuth = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        const tokens = await authService.refreshToken(refreshToken);
        saveTokens(tokens);
      } catch (error) {
        clearTokens();
        throw error;
      }
    } else {
      clearTokens();
      throw new Error('No refresh token available');
    }
  };

  useEffect(() => {
    setSessionClearHandler(() => setUser(null));
    return () => setSessionClearHandler(() => {});
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (accessToken && refreshToken) {
        try {
          // Try to get profile to verify token is valid
          const profile = await authService.getProfile();
          setUser(profile);
        } catch (error) {
          // Token might be expired, try to refresh
          try {
            await refreshAuth();
            // User is already set in saveTokens
          } catch (refreshError) {
            clearTokens();
          }
        }
      } else {
        clearTokens();
      }
      setIsLoading(false);
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    signup,
    logout,
    verifyEmail,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
